import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-oder-wordpress";
const TITLE = "Shopify oder WordPress: Shop oder Website mit Shop?";
const DESCRIPTION =
  "Shopify oder WordPress mit WooCommerce: Betrieb, Wartung, Sicherheit, Kosten und Erweiterbarkeit im sachlichen Vergleich. Mit Empfehlung je nach Schwerpunkt.";
const UPDATED = "2026-09-12";

const ANSWER =
  "WordPress ist ein Content-System, das über WooCommerce verkaufen kann. Shopify ist ein Verkaufssystem, das auch Inhalte kann. Liegt Ihr Schwerpunkt auf Redaktion, Ratgeberinhalten und Individualität und Sie haben jemanden für Wartung, passt WordPress. Steht der Umsatz im Mittelpunkt und soll der Betrieb wartungsarm bleiben, ist Shopify die ruhigere Wahl.";

const faqs = [
  {
    q: "Was ist der Hauptunterschied?",
    a: "Bei WordPress verantworten Sie Hosting, Updates, Sicherheit und Zusammenspiel der Plugins selbst. Bei Shopify übernimmt die Plattform Betrieb, Updates und Zahlungsinfrastruktur, dafür bewegen Sie sich in deren Rahmen.",
  },
  {
    q: "Ist WordPress günstiger?",
    a: "Die Lizenz ist kostenlos, der Betrieb nicht. Hosting, Premium-Plugins, Wartung und Fehlerbehebung summieren sich. Die ehrliche Rechnung vergleicht Gesamtkosten über zwei Jahre, nicht Grundgebühren.",
  },
  {
    q: "Was ist besser für SEO?",
    a: "Beide Systeme können gut ranken. WordPress bietet mehr Freiheit bei Inhaltsstrukturen, Shopify liefert eine solide technische Basis ohne Pflegeaufwand. Den Ausschlag geben Inhalte und Struktur, nicht das System.",
  },
  {
    q: "Kann ich Inhalte behalten und nur den Shop umziehen?",
    a: "Ja, das ist ein üblicher Weg. Der Blog bleibt bestehen, der Verkauf zieht um. Wichtig sind saubere Weiterleitungen und eine klare Domainstruktur.",
  },
  {
    q: "Was kostet ein Wechsel zu Shopify?",
    a: "Der Aufwand hängt an Sortimentsgröße, Sonderlogik und Weiterleitungen. Nach Sichtung nenne ich einen festen Rahmen, statt offen nach Stunden zu schätzen.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-oder-wordpress")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopsysteme",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Vergleich"
      title="Shopify oder WordPress: wer trägt den Betrieb?"
      intro="Die Systemfrage ist im Kern eine Betriebsfrage. Wer Updates, Sicherheit und das Zusammenspiel von Plugins übernimmt, entscheidet mehr über die Gesamtkosten als jede Funktionsliste."
      source="ratgeber-shopify-oder-wordpress"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Zwei unterschiedliche Ausgangspunkte",
          p: [
            "WordPress kommt aus der Welt der Inhalte. Der Shop entsteht über WooCommerce und weitere Plugins. Das ergibt große Freiheit und viele bewegliche Teile.",
            "Shopify kommt aus der Welt des Verkaufs. Produkte, Varianten, Versand, Steuern und Checkout sind fester Bestandteil, dafür arbeiten Sie innerhalb eines definierten Rahmens.",
          ],
        },
        {
          h: "Was der Betrieb tatsächlich verlangt",
          p: [
            "Ein WooCommerce-Shop braucht laufende Aufmerksamkeit. Plugin-Updates können sich gegenseitig stören, Sicherheitslücken müssen zeitnah geschlossen werden, und Hosting will passend dimensioniert sein.",
          ],
          list: [
            "Regelmäßige Updates von Kern, Theme und Plugins",
            "Backups und ein getesteter Wiederherstellungsweg",
            "Sicherheit und Zugriffsverwaltung",
            "Performance bei wachsendem Sortiment und Traffic",
            "Ein Ansprechpartner, wenn nach einem Update etwas bricht",
          ],
        },
        {
          h: "Wann WordPress die richtige Wahl bleibt",
          p: [
            "Wenn Inhalte das Geschäft tragen, viele redaktionelle Formate nötig sind oder sehr spezielle Logik abgebildet wird, spielt WordPress seine Freiheit aus. Voraussetzung ist, dass jemand die Wartung verlässlich übernimmt.",
          ],
        },
        {
          h: "Wann Shopify die ruhigere Wahl ist",
          p: [
            "Wenn der Shop verkaufen soll und Ihre Zeit besser in Sortiment und Marketing fließt, nimmt Shopify den technischen Betrieb aus der Gleichung. Anpassungen passieren im Theme, ohne dass das Fundament wackelt.",
            "Ein Blog ist dabei kein Ausschlusskriterium, für die meisten Shops reicht die integrierte Lösung völlig aus.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-vs-woocommerce", label: "Shopify vs. WooCommerce" },
        { to: "/blog/woocommerce-zu-shopify-migration", label: "WooCommerce zu Shopify" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/kontakt", label: "Wechsel besprechen" },
      ]}
    />
  );
}
